import type { UserInfo, AuthListInfo } from '/#/store';
import type { ErrorMessageMode } from '/#/axios';
import { defineStore } from 'pinia';
import { store } from '/@/store';
import { RoleEnum } from '/@/enums/roleEnum';
import { PageEnum } from '/@/enums/pageEnum';
import { ROLES_KEY, TOKEN_KEY, USER_INFO_KEY, AUTH_LIST_KEY } from '/@/enums/cacheEnum';
import { getAuthCache, setAuthCache } from '/@/utils/auth';
import { GetUserInfoModel, LoginParams } from '/@/api/sys/model/userModel';
import { loginApi, LogoutApi } from '/@/api/sys/user';
import { useI18n } from '/@/hooks/web/useI18n';
import { useMessage } from '/@/hooks/web/useMessage';
import { router } from '/@/router';
import { usePermissionStore } from '/@/store/modules/permission';
import { RouteRecordRaw } from 'vue-router';
import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';
import { isArray } from '/@/utils/is';
import { h } from 'vue';

import avatar from '../../assets/images/avatar.png';
import { getUserAuthListApi } from '/@/api/sys/common';
import { Console } from 'console';
// let user: any = {};

interface UserState {
  userInfo: Nullable<UserInfo>;
  token?: string;
  authList: Nullable<AuthListInfo>;
  roleList: RoleEnum[];
  sessionTimeout?: boolean;
  lastUpdateTime: number;
}

export const useUserStore = defineStore({
  id: 'app-user',
  state: (): UserState => ({
    // user info
    userInfo: null,
    // token
    token: undefined,
    // 权限组 authList
    authList: null,
    // roleList
    roleList: [],
    // Whether the login expired
    sessionTimeout: false,
    // Last fetch time
    lastUpdateTime: 0,
  }),
  getters: {
    getUserInfo(): UserInfo {
      return this.userInfo || getAuthCache<UserInfo>(USER_INFO_KEY) || {};
    },
    getToken(): string {
      return this.token || getAuthCache<string>(TOKEN_KEY);
    },
    getAuthList(): AuthListInfo {
      return this.authList || getAuthCache<AuthListInfo>(AUTH_LIST_KEY) || {};
    },
    getRoleList(): RoleEnum[] {
      return this.roleList.length > 0 ? this.roleList : getAuthCache<RoleEnum[]>(ROLES_KEY);
    },
    getSessionTimeout(): boolean {
      return !!this.sessionTimeout;
    },
    getLastUpdateTime(): number {
      return this.lastUpdateTime;
    },
  },
  actions: {
    setToken(info: string | undefined) {
      this.token = info ? info : ''; // for null or undefined value
      setAuthCache(TOKEN_KEY, info);
    },
    setAuthList(authList: AuthListInfo | null) {
      this.authList = authList;
      setAuthCache(AUTH_LIST_KEY, authList);
    },
    setRoleList(roleList: RoleEnum[]) {
      this.roleList = roleList;
      setAuthCache(ROLES_KEY, roleList);
    },
    setUserInfo(info: UserInfo | null) {
      this.userInfo = info;
      this.lastUpdateTime = new Date().getTime();
      setAuthCache(USER_INFO_KEY, info);
    },
    setSessionTimeout(flag: boolean) {
      this.sessionTimeout = flag;
    },
    resetState() {
      this.userInfo = null;
      this.token = '';
      this.roleList = [];
      this.sessionTimeout = false;
      this.authList = null;
    },
    /**
     * @description: login
     */

    async login(
      params: LoginParams & {
        goHome?: boolean;
        mode?: ErrorMessageMode;
      },
    ) {
      try {
        const { goHome = true, mode, ...loginParams } = params;
        const data = await loginApi(loginParams, mode);
        const { accessToken, user } = data;
        // save token
        this.setToken(accessToken);
        localStorage.setItem('userInfo', JSON.stringify(user));
        const authList = (await getUserAuthListApi()).authList;
        this.setAuthList(authList);
        return this.afterLoginAction(goHome); // 登录操作后
      } catch (error) {
        console.log('[ login error ] >', error);
      }
    },
    async afterLoginAction(goHome?: boolean): Promise<GetUserInfoModel | null> {
      if (!this.getToken) return null;
      // get user info
      const userInfo = await this.getUserInfoAction();

      const sessionTimeout = this.sessionTimeout;
      if (sessionTimeout) {
        this.setSessionTimeout(false);
      } else {
        const permissionStore = usePermissionStore();
        if (!permissionStore.isDynamicAddedRoute) {
          const routes = await permissionStore.buildRoutesAction();
          routes.forEach((route) => {
            router.addRoute(route as unknown as RouteRecordRaw);
          });
          router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);
          permissionStore.setDynamicAddedRoute(true);
        }
        goHome && (await router.replace(userInfo?.homePath || PageEnum.BASE_HOME));
      }
      // return null;
      return userInfo;
    },
    // 获取用户信息操作
    async getUserInfoAction(): Promise<UserInfo | null> {
      if (!this.getToken) return null;
      const strUser = localStorage.getItem('userInfo') || '{}';
      const user = JSON.parse(strUser);
      const userInfo = {
        userId: user.weComUserId,
        username: user.realname,
        realName: user.realname,
        structureId: user.structureId,
        avatar,
        mobile: user.mobile,
        sysUsername: user.realname,
        desc: 'manager',
        password: '',
        token: 'fakeToken1',
        homePath: '/clue', // 用户默认首页
        roles: [
          {
            roleName: 'Super Admin',
            value: 'super',
          },
        ],
      };
      const { roles = [] } = userInfo;
      if (isArray(roles)) {
        const roleList = roles.map((item) => item.value) as RoleEnum[];
        this.setRoleList(roleList);
      } else {
        userInfo.roles = [];
        this.setRoleList([]);
      }
      this.setUserInfo(userInfo);
      return userInfo;
    },
    /**
     * @description: logout
     */
    async logout(goLogin = false, goLogout = false) {
      console.log('[ this.getToke ] >', this.getToken);
      // 跳转单点登录 VITE_SINGLE_LOGIN为true则跳转
      if (this.getToken) {
        this.setToken(undefined);
        this.setAuthList(null);
        this.setSessionTimeout(false);
        this.setUserInfo(null);
        goLogin && router.push(PageEnum.BASE_LOGIN);
        location.reload();
      }
      if (import.meta.env.VITE_SINGLE_LOGIN == 'true') {
        if (goLogout) {
          await LogoutApi();
        }
        window.location.href =
          import.meta.env.VITE_SSO_LOGIN_URL + '?app_key=0c24779339824516bae27e4e69819b9b';
      }
    },

    /**
     * @description: Confirm before logging out
     */
    confirmLoginOut() {
      const { createConfirm } = useMessage();
      const { t } = useI18n();
      createConfirm({
        iconType: 'warning',
        title: () => h('span', t('sys.app.logoutTip')),
        content: () => h('span', t('sys.app.logoutMessage')),
        onOk: async () => {
          await this.logout(true, true);
        },
      });
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store);
}
